import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { normalizeLadder } from '../utils/normalizeLadder';
import type { LadderTier, NormalizedTier } from '../utils/normalizeLadder';
import { formatMultiplier } from '../utils/countModeHourlyPremiumUtils';
import { SurchargeMethodPanel } from '@/components/config_forms/shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '@/components/config_forms/shared/surcharge/useMethodSwapAnimation';
import { TIMELINE_CAPTION_TEXT_CLASS } from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';

function getTierAbbrev(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

export interface CountModeHourlyPremiumOTSplitProps {
  premiumType: 'fixed' | 'percentage';
  premiumValue: number;
  premiumBasis: 'base_rate' | 'ot_inclusive';
  ladder: LadderTier[];
}

interface OTSplitBarProps {
  premiumType: 'fixed' | 'percentage';
  premiumValue: number;
  normalizedTiers: NormalizedTier[];
}

const CountModeOTSplitBar: React.FC<OTSplitBarProps> = ({
  premiumType,
  premiumValue,
  normalizedTiers,
}) => {
  const totalFlex = normalizedTiers.reduce((sum, t) => sum + t.flex, 0);

  return (
    <div
      className="mt-3"
      data-testid="count-mode-ot-split"
      data-anim-audit="count-mode-ot-split"
      data-anim-audit-state={`${premiumType}|${normalizedTiers.length}`}
    >
      <div className="max-w-[380px] mx-auto">
        {/* Header */}
        <div className="text-center mb-2">
          <span
            className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]"
            style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 50%)' }}
          >
            Your shift
          </span>
        </div>

        {/* Tier badges — use abbreviations when 3+ tiers to fit narrow regions */}
        <div className="flex mb-1">
          {normalizedTiers.map((tier, i) => {
            const isLast = i === normalizedTiers.length - 1;
            const useAbbrev = normalizedTiers.length >= 3;
            const badgeText = tier.isCollapsed
              ? tier.name
              : useAbbrev
                ? `${getTierAbbrev(tier.name)} (${formatMultiplier(tier.multiplier)}×)`
                : `${tier.name} (${formatMultiplier(tier.multiplier)}×)`;
            return (
              <React.Fragment key={tier.code}>
                <div
                  className="text-center min-w-0 px-1"
                  style={{ flex: tier.flex }}
                >
                  <span
                    className="inline-block max-w-full truncate bg-surface-1 border rounded-[0.25rem] px-2 py-1 font-mono text-[10px] sm:text-[12px]"
                    style={{
                      borderColor: `color-mix(in srgb, var(--primary), transparent ${75 - i * 15}%)`,
                      color: `color-mix(in srgb, var(--primary), transparent ${40 - i * 10}%)`,
                    }}
                    title={tier.isCollapsed ? tier.name : `${tier.name} (${formatMultiplier(tier.multiplier)}×)`}
                  >
                    {badgeText}
                  </span>
                </div>
                {!isLast && <div className="w-0.5 flex-none" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Split bar */}
        <div
          className="relative h-14 rounded-[0.25rem] overflow-hidden"
          style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary), transparent 70%)' }}
        >
          {(() => {
            // Precompute cumulative left positions to avoid O(n²) slicing
            const cumLefts: number[] = [];
            let cumFlex = 0;
            for (const t of normalizedTiers) { cumLefts.push(cumFlex); cumFlex += t.flex; }
            return normalizedTiers.map((tier, i) => {
            const leftPct = (cumLefts[i] / totalFlex) * 100;
            const widthPct = (tier.flex / totalFlex) * 100;
            const isLast = i === normalizedTiers.length - 1;
            const bgOpacity = (0.04 + i * 0.03).toFixed(2);
            const abbrev = tier.isCollapsed ? tier.name : getTierAbbrev(tier.name);
            const textOpacity = Math.max(10, 30 - i * 10);
            const subOpacity = Math.max(30, 60 - i * 10);

            return (
              <React.Fragment key={tier.code}>
                {/* Region */}
                <div
                  className="absolute top-0 bottom-0 flex flex-col items-center justify-center gap-1 overflow-hidden px-1"
                  style={{
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    backgroundColor: `color-mix(in srgb, var(--primary), transparent ${100 - Math.round(parseFloat(bgOpacity) * 100)}%)`,
                  }}
                >
                  {tier.isCollapsed ? (
                    <span
                      className="font-mono text-[10px] sm:text-[12px] truncate max-w-full"
                      style={{ color: `color-mix(in srgb, var(--primary), transparent ${textOpacity}%)` }}
                    >
                      {tier.name}
                    </span>
                  ) : (
                    <>
                      <span
                        className="font-mono text-[10px] sm:text-[12px] font-semibold truncate max-w-full"
                        style={{ color: `color-mix(in srgb, var(--primary), transparent ${textOpacity}%)` }}
                      >
                        {premiumType === 'percentage'
                          ? `${abbrev} hours × ${premiumValue}%`
                          : `${abbrev} hours × $${premiumValue}/hr`}
                      </span>
                      <span
                        className="font-mono text-[10px] sm:text-[12px] truncate max-w-full"
                        style={{ color: `color-mix(in srgb, var(--primary), transparent ${subOpacity}%)` }}
                      >
                        × {formatMultiplier(tier.multiplier)}
                      </span>
                    </>
                  )}
                </div>
                {/* Divider */}
                {!isLast && (
                  <div
                    className="absolute top-0 bottom-0"
                    style={{
                      left: `calc(${leftPct + widthPct}% - 1px)`,
                      width: '2px',
                      background:
                        'linear-gradient(to bottom, color-mix(in srgb, var(--color-foreground), transparent 70%), color-mix(in srgb, var(--color-foreground), transparent 90%))',
                    }}
                  />
                )}
              </React.Fragment>
            );
          });
          })()}
        </div>

        {/* Caption */}
        <div className={`mt-2 text-center ${TIMELINE_CAPTION_TEXT_CLASS}`}>
          Bracket proportions reflect your rate multiplier thresholds.
        </div>
      </div>
    </div>
  );
};

export const CountModeHourlyPremiumOTSection: React.FC<CountModeHourlyPremiumOTSplitProps> = ({
  premiumType,
  premiumValue,
  premiumBasis,
  ladder,
}) => {
  const normalized = premiumBasis === 'ot_inclusive' ? normalizeLadder(ladder) : null;
  const showOTDetails =
    normalized != null &&
    normalized.length >= 2 &&
    normalized.some((t, i) => i > 0 && t.multiplier > normalized[i - 1].multiplier);

  const panelKey = showOTDetails ? 'count-ot-visible' : 'count-ot-hidden';
  const { containerStyle, containerRef, activePanelRef, fadeDuration } =
    useMethodSwapAnimation(panelKey);

  return (
    <div
      ref={containerRef}
      className="overflow-hidden"
      style={containerStyle}
      data-testid="count-mode-ot-section"
      data-anim-audit="count-mode-ot-section-container"
      data-anim-audit-open={showOTDetails ? 'true' : 'false'}
    >
      <div className="relative grid">
        <AnimatePresence mode="sync" initial={false}>
          {showOTDetails ? (
            <SurchargeMethodPanel
              key="count-ot-visible"
              activePanelRef={activePanelRef}
              transitionDuration={fadeDuration}
            >
              <div data-anim-audit="count-mode-ot-section-panel" data-anim-audit-open="true">
                <CountModeOTSplitBar
                  premiumType={premiumType}
                  premiumValue={premiumValue}
                  normalizedTiers={normalized!}
                />
              </div>
            </SurchargeMethodPanel>
          ) : (
            <SurchargeMethodPanel
              key="count-ot-hidden"
              activePanelRef={activePanelRef}
              transitionDuration={fadeDuration}
            >
              <div
                className="h-0 overflow-hidden"
                data-anim-audit="count-mode-ot-section-panel"
                data-anim-audit-open="false"
                aria-hidden
              />
            </SurchargeMethodPanel>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
